import { useApp } from '@context/AppContext';
import {
  Copy, ClipboardPaste, FlipHorizontal, FlipVertical, Trash2, RotateCcw, RotateCw,
  Maximize2, Minimize2,
} from 'lucide-react';

/**
 * Floating toolbar for selection actions + paste.
 *
 * Two modes:
 * 1. Strokes selected → full toolbar: copy, paste, flip, rotate, scale, delete
 * 2. Nothing selected but clipboard has content → paste-only bar
 *
 * Positioned at the bottom-centre of the screen for easy thumb access.
 */
export default function SelectionToolbar({ onCopy, onPaste, onFlip, onFlipVertical, onRotate, onScale, onDelete, hasClipboard, onClearClipboard }) {
  const { state } = useApp();
  const hasSelection = state.canvas.selectedStrokeIds.length > 0;

  // Nothing selected and nothing in clipboard — hide entirely
  if (!hasSelection && !hasClipboard) return null;

  const count = state.canvas.selectedStrokeIds.length;

  const btn = (onClick, icon, title, variant = 'default') => (
    <button
      onClick={onClick}
      title={title}
      className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
        variant === 'danger'
          ? 'text-red-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100'
          : variant === 'primary'
            ? 'text-amber hover:text-amber-dark hover:bg-amber/10 active:bg-amber/20'
            : 'text-slate-600 hover:text-charcoal hover:bg-slate-100 active:bg-slate-200'
      }`}
    >
      {icon}
    </button>
  );

  // Paste-only mode — nothing selected but clipboard has content
  if (!hasSelection && hasClipboard) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 animate-[fadeInUp_0.2s_ease-out] flex items-center gap-2">
        <button
          onClick={onPaste}
          className="flex items-center gap-2 px-4 py-2.5 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200 text-sm font-display font-medium text-amber hover:bg-amber/5 active:bg-amber/10 transition-colors"
        >
          <ClipboardPaste className="w-4 h-4" />
          Paste copied items
        </button>
        <button
          onClick={onClearClipboard}
          title="Dismiss"
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/95 backdrop-blur-sm shadow-xl border border-slate-200 text-slate-400 hover:text-charcoal transition-colors text-xs"
        >
          ✕
        </button>
        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translate(-50%, 10px); }
            to { opacity: 1; transform: translate(-50%, 0); }
          }
        `}</style>
      </div>
    );
  }

  // Full selection toolbar
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 animate-[fadeInUp_0.2s_ease-out]">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200 px-2 py-1.5 flex items-center gap-0.5">
        {/* Count badge */}
        <div className="px-2 py-1 text-xs font-mono text-amber font-medium">
          {count}
        </div>

        <div className="w-px h-6 bg-slate-200" />

        {/* Copy */}
        {btn(onCopy, <Copy className="w-4 h-4" />, 'Copy')}

        {/* Paste (if clipboard has content) */}
        {hasClipboard && btn(onPaste, <ClipboardPaste className="w-4 h-4" />, 'Paste', 'primary')}

        <div className="w-px h-6 bg-slate-200" />

        {/* Flip */}
        {btn(() => onFlip(), <FlipHorizontal className="w-4 h-4" />, 'Flip horizontal')}
        {btn(() => onFlipVertical(), <FlipVertical className="w-4 h-4" />, 'Flip vertical')}

        {/* Rotate */}
        {btn(() => onRotate(-15), <RotateCcw className="w-4 h-4" />, 'Rotate left')}
        {btn(() => onRotate(15), <RotateCw className="w-4 h-4" />, 'Rotate right')}

        {/* Scale */}
        {btn(() => onScale(1.2), <Maximize2 className="w-4 h-4" />, 'Scale up')}
        {btn(() => onScale(0.8), <Minimize2 className="w-4 h-4" />, 'Scale down')}

        <div className="w-px h-6 bg-slate-200" />

        {/* Delete */}
        {btn(onDelete, <Trash2 className="w-4 h-4" />, 'Delete', 'danger')}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
